import {
  type RiskAssessmentResultConfig,
  riskAssessmentResultConfigSchema,
} from '@risksmart-app/domain/src/types';
import type { GetLatestRiskAssessmentResultConfigResponse } from '@risksmart-app/trpc/src/types';
import type { GetLatestRiskAssessmentResultConfigQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetLatestRiskAssessmentResultConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { createQueryHook } from 'src/utils';

import type { ConfigType } from './types';

export type { RiskAssessmentResultConfig };
export { riskAssessmentResultConfigSchema };

type UseGetLatestArgs = {
  type: ConfigType;
};

const useGetLatestBase = createQueryHook<
  UseGetLatestArgs,
  GetLatestRiskAssessmentResultConfigResponse[],
  GetLatestRiskAssessmentResultConfigQuery
>({
  trpcQueryOptions: (trpc, { type }) =>
    trpc.frontend.riskAssessmentResultConfig.getLatest.queryOptions({
      Type: type,
    }),
  mapTrpcDataToGraphQL: (data) => ({
    risk_assessment_result_config: data ?? [],
  }),
  graphqlDocument: GetLatestRiskAssessmentResultConfigDocument,
  graphqlVariables: ({ type }) => ({ Type: type }),
  graphqlFetchPolicy: 'cache-and-network',
});

export const useGetLatestRiskAssessmentResultConfig = (options: {
  type: ConfigType;
  skip?: boolean;
}) => {
  const { data, loading, error, refetch } = useGetLatestBase({
    queryArgs: { type: options.type },
    shouldSkip: options.skip,
  });

  const record = data?.risk_assessment_result_config[0];
  const config = useMemo(
    () =>
      record?.Config
        ? riskAssessmentResultConfigSchema.parse(record.Config)
        : undefined,
    [record?.Config]
  );

  return {
    config,
    loading,
    error,
    id: record?.Id,
    originalTimestamp: record?.ModifiedAtTimestamp,
    refetch,
  };
};
